import Vue from 'vue';
import { mapGetters } from 'vuex';
import _ from 'lodash';
import { guid } from '@/mixins/guid';
import { modals } from '@/mixins/modal';
import { uploader } from '@/mixins/aws/uploader/s3';
import uploadProgress from '@/mixins/aws/uploader';
import linkPage from '@editor/droplets/_bubble-options/components/link-page';
import path from 'path';

export default Vue.component('edit-content-slider', {
  template: require('./template.html'),
  components: {
    linkPage,
    uploadProgress,
  },
  computed: {
    ...mapGetters(['viewport', 'currentPage', 'currentSite']),
    content() {
      return this.droplet.content;
    },
    minCount() {
      const total = this.droplet.content.length;
      let disableClass = false;
      if (total <= 2) {
        disableClass = true;
      }
      return disableClass;
    },
    maxCount() {
      const total = this.droplet.content.length;
      let disableClass = false;
      if (total >= 10) {
        disableClass = true;
      }
      return disableClass;
    },
  },
  data() {
    return {
      paginate: ['content'],
      showImgModal: false,
      activeItem: null,
      loading: false,
      prefix: 'drz_content_slider_',
      bucketPath: `_droplet-assets/contentSlider/${this.droplet._id}/`,
      thumbnails: `_droplet-assets/_thumbnails/contentSlider/${this.droplet._id}/`,
      delayInput: null,
      effect: this.droplet.effect,
      effects: [{ effect: 'Fade', value: 'fade' }, { effect: 'Slide', value: 'slide' }],
      bulletPosition: this.droplet.bulletPosition,
      bulletPositions: [
        { position: 'Bottom', value: 'bottom' },
        { position: 'Right', value: 'right' },
        { position: 'Left', value: 'left' },
        { position: 'Hide', value: 'hide' },
      ],
      activeImage: null,
    };
  },
  name: 'edit-content-slider',
  mixins: [guid, modals, uploader],
  props: ['droplet', 'show-edit-bubble'],
  methods: {
    getThumbnail(image) {
      let thumb = this._getThumbFromFile(image.src);
      if (image.src.startsWith('/')) {
        thumb = `${this._servePrefix(image.src)}/${this.thumbnails}${thumb}`;
      }
      return `url('${thumb}')`;
    },
    openUpload() {
      this.$refs.uploadFile.click();
      // reset local rearranges on open
      this.$store.dispatch('setContentSliderLimits', {
        drp: this.droplet,
        showAll: false,
        itemLimit: 5,
      });
    },
    async uploadImages(e) {
      this.$validator.validateAll().then(async (result) => {
        // run only if file types and dimensions are correct
        if (result) {
          this.loading = true;
          const newFiles = [...e.target.files];
          if (newFiles.length > 0) {
            if (!this.droplet.firstUpload) {
              const payload = {
                drp: this.droplet,
                value: true,
              };
              await this.$store.dispatch('setContentSliderFirst', payload);
            }
            try {
              // check for expired sessions first
              await this._cognitoCheckSession();
              await Promise.all([...newFiles].map(async (f) => {
                let fileName = path.basename(f.name);
                fileName = `${this.prefix}${_.trim(fileName)}`;
                fileName = _.replace(fileName, /\s+/g, '_');
                const filePath = `/${this.bucketPath}${fileName}`;
                await this.uploadFile({
                  file: `${this._sitePath}${filePath}`,
                  body: f,
                  thumbnail: {
                    width: 35,
                    srcFile: f,
                    name: fileName,
                    bucketPath: `${this._sitePath}/${this.thumbnails}`,
                  },
                });
                // update image array
                await this.$store.dispatch('addContentSliderItem', {
                  drp: this.droplet,
                  newContent: {
                    _id: this.guid(),
                    src: filePath,
                    altText: path.basename(filePath),
                    href: false,
                    newWindow: false,
                    reference: false,
                  },
                });
              }));
            } catch (err) {
              if (!err.message.match(/aborted/gi)) {
                this.$log.error(err);
                await this._reportError({
                  error: err,
                  fn: 'uploadImages',
                });
              }
            } finally {
              this.loading = false;
              this.bouncePlugin();
            }
          }
          // reset value to null so we can get the "change" event to run
          // in any case. Even when the user selects the same file
          e.target.value = null;
        }
      });
    },
    async showAllImages(e) {
      const checked = e.currentTarget.checked;
      const payload = {};
      payload.drp = this.droplet;
      payload.showAll = checked;
      if (checked) {
        payload.itemLimit = this.droplet.content.length;
      } else {
        payload.itemLimit = 5;
      }
      await this.$store.dispatch('setContentSliderLimits', payload);
      this.bouncePlugin();
    },
    getImg(image) {
      return path.basename(image.src);
    },
    findIndex(id) {
      // we need to find the index from original array since
      // the template is looping through the paginated array
      let i;
      _.forEach(this.droplet.content, (item, index) => {
        if (item._id === id) {
          i = index;
        }
      });
      return {
        index: i,
        template: i + 1,
      };
    },
    confirmDelete(item) {
      const total = this.droplet.content.length;
      if (total > 2) {
        this.activeItem = item;
        this.toggleModal();
      }
    },
    async removeContentItem(item) {
      const total = this.droplet.content.length;
      if (total > 2) {
        const payload = {};
        payload.drp = this.droplet;
        payload.contIndex = _.findIndex(this.droplet.content, c => c._id === item._id);
        // remove item then bounce plugin
        await this.$store.dispatch('removeContentSliderItem', payload);
        this.bouncePlugin();

        // send off to be deleted from s3 if not default images
        if (this.droplet.firstUpload) {
          this.loading = true;
          // prep notifcation type
          const notif = {
            id: this.guid(),
          };
          const file = path.basename(item.src);
          const thumb = this._getThumbFromFile(item.src);
          try {
            // delete original from s3
            await this._s3deleteFile({
              key: `${this._fullSiteFolder}/${this.bucketPath}${file}`,
            });
            // delete thumbnail from s3
            await this._s3deleteFile({
              key: `${this._fullSiteFolder}/${this.thumbnails}${thumb}`,
            });
            // set for notifications
            notif.type = 'success';
            notif.message = `Successfully deleted image: ${file}`;
          } catch (err) {
            this.$log.error('error: ', err);
            // set for notifications
            notif.type = 'error';
            notif.message = `${err.message || err}. This has been reported.`;
            await this._reportError({
              error: err,
              fn: 'removeContentItem',
            });
          } finally {
            // send off to notifications
            this.$store.dispatch('addNotification', notif);
            // kill loader
            this.loading = false;
            this.bouncePlugin();
          }
        }
      }
    },
    setAltText(e, image) {
      const val = this._getInputVal(e.target.value);
      this.$store.dispatch('setDropletVal', {
        drp: image,
        key: 'altText',
        val,
      });
    },
    getImgName(name) {
      return path.basename(name);
    },
    setDelay(e) {
      const val = e.currentTarget.value;
      this.delayInput = val;
      const payload = {};
      payload.drp = this.droplet;
      payload.delay = val;
      if (val > 0) {
        this.promise(this.$store.dispatch('setContentSliderDelay', payload), 'setting delay');
      }
    },
    setEffect(val) {
      const payload = {};
      payload.drp = this.droplet;
      payload.effect = val;
      this.promise(this.$store.dispatch('setContentSliderEffect', payload), 'setting effect');
    },
    setBulletPosition(val) {
      const payload = {};
      payload.drp = this.droplet;
      payload.bulletPosition = val;
      this.promise(
        this.$store.dispatch('setContentSliderBullets', payload),
        'setting bullet position',
      );
    },
    setControls(e) {
      const hidden = e.currentTarget.checked;
      const payload = {};
      payload.drp = this.droplet;
      if (hidden) {
        payload.controls = 'hide';
      } else {
        payload.controls = 'show';
      }
      this.promise(this.$store.dispatch('setContentSliderControls', payload), 'setting controls');
    },
    bouncePlugin() {
      this.$vnode.context.unBindPlugin();
      this.$vnode.context.bindPlugin();
    },
    async drzRevertData() {
      // rebind plugin in case user removed items then cancelled.
      this.$emit('css-generator');
      this.effect = this.droplet.effect;
      this.bulletPosition = this.droplet.bulletPosition;
      this.bouncePlugin();
      await this._cognitoCheckSession();
      if (this.droplet.firstUpload) {
        const content = this.droplet.content;
        _.forEach(content, async (item, i) => {
          if (!this._isBoilerplateAsset(item.src)) {
            let file = path.basename(item.src);
            file = `${this._sitePath}/${this.bucketPath}${file}`;
            try {
              await this._s3checkFile({ path: file });
            } catch (e) {
              this.$log.warn(e);
              // this is for the event in which a user deletes
              // an image from s3 then tries to cancel. Since we
              // aren't restoring the image in s3, we need to
              // remove it from the content array after the cancel
              // action reverts the other data.
              const payload = {};
              payload.drp = this.droplet;
              payload.contIndex = i;
              // remove image then bounce plugin
              await this.$store.dispatch('removeContentSliderItem', payload);
              // give the jquery plugin half a second to bounce
              setTimeout(() => {
                this.bouncePlugin();
              }, 500);
            }
          }
        });
      }
    },
    setLocals(v) {
      this.$emit('set-display', v);
    },
    toggleModal() {
      this.showImgModal = !this.showImgModal;
      const content = this.$refs.confirmModal;
      if (this.showImgModal) {
        this.modelMoveOut(content);
      } else {
        this.modalMoveIn(content);
      }
    },
    formatErrors(ers) {
      return ers.join(' ');
    },
    promise(action, error) {
      const promise = new Promise((resolve, reject) => {
        if (action) {
          resolve();
        } else {
          reject(Error(`Error in content slider: ${error}`));
        }
      });
      promise.then(() => {
        this.bouncePlugin();
      });
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/editor/droplets/content-slider/components/_edit-options/index.js